/**
 * Capacitor Mobile App Initialization
 *
 * Configures Capacitor plugins for iOS and Android apps
 * Handles status bar, splash screen, keyboard, deep linking, and other mobile features
 */

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { App as CapacitorApp } from '@capacitor/app';
import {
  externalAppUrlToRouterPath,
  isNativeDeepLinkDebounced,
  isRedundantReviewDeepLink,
} from './services/deepLinkService';
import { warmUpMovieFunctions } from './services/tmdbFunctionsApi';
import { SearchService } from './services/searchService';

// Deep link navigation callback - set by React app after Router mounts
let deepLinkNavigator: ((path: string) => void) | null = null;
const pendingDeepLinkPaths: string[] = [];

function flushPendingDeepLinks(): void {
  if (!deepLinkNavigator || pendingDeepLinkPaths.length === 0) return;
  while (pendingDeepLinkPaths.length > 0) {
    const to = pendingDeepLinkPaths.shift();
    if (!to) continue;
    if (isRedundantReviewDeepLink(to)) {
      console.log('🔗 Skipping pending deep link — already on movie with this highlightReview:', to);
      continue;
    }
    if (isNativeDeepLinkDebounced(to)) {
      console.log('🔗 Skipping pending deep link (debounced):', to);
      continue;
    }
    try {
      // markNativeDeepLinkHandled() runs inside the registered navigator now,
      // so it's only recorded if the navigator actually proceeds (not skipped
      // by the session-aware re-delivery guard).
      deepLinkNavigator(to);
    } catch (e) {
      console.error('Deep link flush failed:', e);
    }
  }
}

/**
 * Set the navigation function for deep links
 * Should be called from React app with useNavigate hook
 */
export function setDeepLinkNavigator(navigator: ((path: string) => void) | null): void {
  deepLinkNavigator = navigator;
  if (navigator) {
    console.log('Deep link navigator registered');
    flushPendingDeepLinks();
  } else {
    console.log('Deep link navigator cleared');
  }
}

/**
 * Navigate from push notification taps on native (iOS/Android).
 * Uses the same React Router callback as universal links — avoids `location.href` full reload,
 * which races the SPA when the app is already in memory (common iOS WKWebView issue).
 */
function normalizePathForCompare(fullPath: string): string {
  const [rawPath, search] = fullPath.split('?');
  const p = rawPath.replace(/\/+$/, '') || '/';
  return search ? `${p}?${search}` : p;
}

export function navigateFromNativePushRoute(fullPath: string): void {
  if (!fullPath?.trim()) return;
  let path = fullPath.trim();
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  console.log('🔗 Push notification route (native SPA):', path);

  if (isRedundantReviewDeepLink(path)) {
    console.log('🔗 Skipping push route — redundant review deep link');
    return;
  }
  if (isNativeDeepLinkDebounced(path)) {
    console.log('🔗 Skipping push route (debounced duplicate)');
    return;
  }

  try {
    const current = `${window.location.pathname}${window.location.search || ''}`;
    if (normalizePathForCompare(current) === normalizePathForCompare(path)) {
      console.log('🔗 Already on push target route, skipping:', path);
      return;
    }
  } catch {
    // non-fatal
  }

  if (deepLinkNavigator) {
    // Marking happens inside the navigator now so a re-delivery on a moved-on
    // user can be skipped instead of yanking them back.
    deepLinkNavigator(path);
  } else {
    pendingDeepLinkPaths.push(path);
    console.log('🔗 Router not mounted yet, queued push route:', path);
  }
}

/**
 * Initialize Capacitor plugins for mobile platforms
 * Should be called early in the app lifecycle
 */
export async function initializeCapacitor() {
  // Only run on native platforms (iOS/Android)
  if (!Capacitor.isNativePlatform()) {
    console.log('Running on web platform, skipping Capacitor initialization');
    return;
  }

  console.log('Initializing Capacitor for native platform:', Capacitor.getPlatform());

  try {
    // Initialize App Check FIRST — before any Firebase usage
    // iOS uses App Attest; Android uses Play Integrity (both automatic)
    await initializeNativeAppCheck();

    // Configure Status Bar
    await configureStatusBar();

    // Configure Splash Screen
    await configureSplashScreen();

    // Configure Keyboard (resize mode is iOS-only; config also sets Keyboard.resize)
    await configureKeyboard();

    // Configure App Lifecycle
    configureAppLifecycle();

    console.log('✅ Capacitor initialization complete');
  } catch (error) {
    console.error('Error initializing Capacitor:', error);
  }
}

/**
 * Initialize Firebase App Check for native platforms.
 * iOS → App Attest; Android release → Play Integrity (native defaults).
 *
 * Android debug APK: the patched plugin forces the App Check debug provider when the
 * app is debuggable (Capacitor web bundles are usually built with NODE_ENV=production,
 * so we cannot rely on debugToken from JS there).
 *
 * IMPORTANT: Register debug tokens in Firebase Console for dev devices:
 * https://console.firebase.google.com/project/watchlists-1c639/appcheck
 */
async function initializeNativeAppCheck() {
  try {
    const { FirebaseAppCheck } = await import('capacitor-firebase-app-check-patched');

    await FirebaseAppCheck.initialize({
      isTokenAutoRefreshEnabled: true,
      // iOS: enables debug provider when using a dev web server / development bundle
      debugToken: process.env.NODE_ENV === 'development',
    });

    console.log('✅ App Check initialized for native:', Capacitor.getPlatform());
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 App Check: iOS dev bundle — register debug token from logs if needed');
    }
  } catch (error) {
    // Non-fatal — app still works but Firestore enforcement will block requests
    console.error('❌ App Check initialization failed on native:', error);
  }
}

/**
 * Configure Status Bar appearance
 */
async function configureStatusBar() {
  try {
    // Set status bar style to light content (white text/icons on dark background)
    // Style.Dark = white icons (for dark backgrounds)
    // Style.Light = black icons (for light backgrounds)
    await StatusBar.setStyle({ style: Style.Dark });

    // Set status bar background color to match app's dark theme
    await StatusBar.setBackgroundColor({ color: '#1C1C1E' });

    // Show the status bar
    await StatusBar.show();

    // Do NOT call `setOverlaysWebView({ overlay: false })` on Android: it can force a white status /
    // nav region when `setBackgroundColor` is skipped (edge-to-edge on Android 15+). Overlap is handled
    // via `html.capacitor-android #root` padding in index.css + initializeSafeAreaHandling().

    console.log('✅ Status bar configured');
  } catch (error) {
    console.error('Error configuring status bar:', error);
  }
}

/**
 * Configure Splash Screen
 * NOTE: Splash screen hiding is now handled by bootCoordinator
 * to ensure Firebase is ready before showing the app.
 */
async function configureSplashScreen() {
  // Splash screen hiding is now coordinated by bootCoordinator.ts
  // This prevents the race condition where splash hides before Firebase is ready
  console.log('✅ Splash screen configured (hiding handled by bootCoordinator)');
}

/**
 * Manually hide splash screen
 * Called by bootCoordinator when ready to show boot screen
 */
export async function hideSplashScreen(fadeOutDuration: number = 300): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await SplashScreen.hide({ fadeOutDuration });
    console.log('✅ Splash screen hidden manually');
  } catch (error) {
    console.error('Error hiding splash screen:', error);
  }
}

/**
 * Configure Keyboard behavior
 */
async function configureKeyboard() {
  try {
    if (Capacitor.getPlatform() === 'ios') {
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
    }

    // Add keyboard event listeners
    Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('Keyboard will show with height:', info.keyboardHeight);
    });

    Keyboard.addListener('keyboardDidShow', () => {
      console.log('Keyboard is now visible');
    });

    Keyboard.addListener('keyboardWillHide', () => {
      console.log('Keyboard will hide');
    });

    Keyboard.addListener('keyboardDidHide', () => {
      console.log('Keyboard is now hidden');
    });

    console.log('✅ Keyboard configured');
  } catch (error) {
    console.error('Error configuring keyboard:', error);
  }
}

/**
 * Configure App Lifecycle events
 */
function configureAppLifecycle() {
  try {
    // Handle app state changes
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Active:', isActive);
      // WKWebView often does not fire document.visibilityState the same as desktop; dispatch so
      // notifications + push token refresh can run when returning from background (esp. iOS).
      if (isActive && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('watchlistsAppResumed'));
        // Re-warm functions each time app comes to foreground — keeps them hot without minInstances cost
        SearchService.warmUp();
        warmUpMovieFunctions();
      }
    });

    // Universal links / custom scheme — SPA navigate only (no location.href reload;
    // reload was racing React Router and often left users on /).
    CapacitorApp.addListener('appUrlOpen', (data) => {
      console.log('🔗 App opened with URL:', data.url);
      const path = externalAppUrlToRouterPath(data.url);
      if (!path) {
        console.warn('⚠️ Unrecognized or empty deep link URL:', data.url);
        return;
      }
      console.log('🔗 Deep link router path:', path);
      console.log('🔗 Current location:', window.location.pathname);
      console.log('🔗 Navigator available:', !!deepLinkNavigator);

      if (isRedundantReviewDeepLink(path)) {
        console.log('🔗 Skipping deep link — already on movie with this highlightReview:', path);
        return;
      }
      if (isNativeDeepLinkDebounced(path)) {
        console.log('🔗 Skipping duplicate deep link (debounced):', path);
        return;
      }
      if (deepLinkNavigator) {
        console.log('✅ Navigating to:', path);
        // Marking happens inside the navigator now so iOS re-delivering the
        // same universal link to a moved-on user is a no-op instead of a yank.
        deepLinkNavigator(path);
        console.log('✅ Navigation called successfully');
      } else {
        console.log('⏳ Navigator not ready, queuing:', path);
        pendingDeepLinkPaths.push(path);
      }
    });

    // NOTE: The Android backButton listener lives in useSwipeBackGesture
    // (mounted at App.tsx). Registering a second listener here used to fire
    // window.history.back() in addition to the hook's navigate(-1), causing a
    // double-back (e.g. /profile/watched → /profile → / on a single press).
    // Keep this comment so future me doesn't reintroduce the duplicate.

    console.log('✅ App lifecycle configured');
  } catch (error) {
    console.error('Error configuring app lifecycle:', error);
  }
}

/**
 * Check if running on native platform
 */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Get current platform (ios, android, or web)
 */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}

/**
 * Check if running on iOS
 */
export function isIOS(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

/**
 * Check if running on Android
 */
export function isAndroid(): boolean {
  return Capacitor.getPlatform() === 'android';
}

/**
 * Check if running on web
 */
export function isWeb(): boolean {
  return Capacitor.getPlatform() === 'web';
}
